import React, { useState } from 'react';

import { TRANSFER_ERROR_CODE } from '../../constants/admission';
import WardBedPicker from './WardBedPicker';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const drawerCls = 'bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 shadow-xl w-full max-w-xl h-full overflow-y-auto ml-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';

/** Every error the frozen ADDENDUM-2 §transfer-bed contract can raise. */
const ERROR_COPY = {
  [TRANSFER_ERROR_CODE.ADMISSION_NOT_FOUND]: 'This admission could not be found. Refresh and try again.',
  [TRANSFER_ERROR_CODE.NO_CURRENT_BED]: 'This admission has no current bed to transfer from — use “Assign bed” instead.',
  [TRANSFER_ERROR_CODE.SAME_BED]: 'That is the patient’s current bed — pick a different bed to transfer to.',
  [TRANSFER_ERROR_CODE.BED_NOT_AVAILABLE]: 'That bed isn’t available right now. Pick another bed.',
  [TRANSFER_ERROR_CODE.BED_OCCUPIED]: 'That bed was just taken. Pick another bed.',
  [TRANSFER_ERROR_CODE.TRANSFER_CONFLICT]: 'This admission’s bed changed in another session. Refresh and try the transfer again.',
  [TRANSFER_ERROR_CODE.ADMISSION_NOT_ACTIVE]: 'This admission is no longer active — bed transfers aren’t allowed.',
};

/**
 * TransferBedDialog (day-1 ADDENDUM-2 · S7/E-transfer) — move a BEDDED
 * admission to a new bed. Reuses `WardBedPicker` (its `isBedSelectable`
 * gate already renders every non-vacant bed, INCLUDING the patient's current
 * one, disabled with a status chip — so a bedded admission's current bed can
 * never be re-picked here; "vacant beds only" per the frozen contract).
 *
 * A 409 `BED_OCCUPIED` / `TRANSFER_CONFLICT` race drops the pick and prompts
 * a re-read (same pattern as `AssignBedDialog`'s `BED_OCCUPIED` handling) —
 * nothing else entered is lost. Rendered via the in-app drawer — no
 * window.confirm/alert.
 */
export default function TransferBedDialog({ admission, onClose, onSubmit }) {
  const [bed, setBed] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!bed || busy) return;
    setBusy(true);
    setError(null);
    const res = await onSubmit({ to_bed_id: bed.bed_id, reason: reason.trim() || undefined });
    setBusy(false);
    if (res && !res.success) {
      if (res.code === TRANSFER_ERROR_CODE.BED_OCCUPIED || res.code === TRANSFER_ERROR_CODE.TRANSFER_CONFLICT) {
        setBed(null); // keep the drawer open, drop the pick, re-pick against fresh state
      }
      setError(ERROR_COPY[res.code] || res.error || 'Could not transfer the bed. Try again.');
    }
    // success → the parent closes + refreshes.
  };

  return (
    <div className={overlayCls} data-testid="transfer-bed-dialog">
      <div className={drawerCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Change bed — {admission.patient_name || admission.patient_id}</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4">
          <div className="mb-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2" data-testid="transfer-current-bed">
            Current bed: <b className="text-slate-900 dark:text-white">{admission.bed_label || '—'}</b>{admission.ward_name ? ` · ${admission.ward_name}` : ''}
          </div>

          {error && (
            <div className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="transfer-bed-error">{error}</div>
          )}

          <WardBedPicker
            anchorDeptId={admission.department_id || null}
            value={bed ? bed.bed_id : null}
            onChange={setBed}
            currentBedId={admission.current_bed_id || null}
          />

          <div className="mt-3">
            <label className={labelCls} htmlFor="transfer-reason">Reason (optional)</label>
            <textarea
              id="transfer-reason"
              className={inputCls}
              rows={2}
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Moving closer to the nursing station"
            />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={submit} disabled={!bed || busy} data-testid="transfer-bed-confirm">
            {busy ? 'Moving…' : 'Move to this bed'}
          </button>
        </div>
      </div>
    </div>
  );
}
